/**
 * 1.7.8 — the bent day on the screen: each card's window, the overlaps the office kept, the time given back and
 * the crew's end of day, from the same rules the server uses (shared/day-bend.ts). Nothing here is stored.
 */
import { useMemo } from 'react';
import type { TodayVisit } from '@shared/api-types';
import { dayPlanFor, gapMinutes, type PlannedPosition } from '@shared/dayplan';
import { freeGaps, overlapsOnDay, windowsOf, type BendState, type BendVisit, type BendWindow, type FreeGap } from '@shared/day-bend';
import { startsVisit } from '@shared/arrival';
import { serviceMinutesNow } from '@shared/dates';
import { useDayPlan } from '@/lib/queries';

const DONE = new Set(['Completed', 'No Show', 'Unable to Complete']);

/** A visit as the rules see it: finished, started (the crew is inside) or still planned. */
export function bendStateOf(v: Pick<TodayVisit, 'status' | 'arrival' | 'closeout'>): BendState {
  if (DONE.has(v.status) || v.closeout) return 'finished';
  return v.status === 'In Progress' || startsVisit(v.arrival?.answer) ? 'started' : 'planned';
}

/** The visits that hold a time on the day (the same ones the day plan lays out). */
export function bendVisitsOf(visits: readonly TodayVisit[]): BendVisit[] {
  const out: BendVisit[] = [];
  for (const v of visits) {
    if (v.position === null || v.kind === 'facility' || v.status === 'Cancelled' || v.status === 'Rescheduled' || v.status === 'Proposed') continue;
    out.push({ id: v.appointmentId, unit: v.unit, position: v.position, tenant: v.kind === 'tenant', state: bendStateOf(v) });
  }
  return out;
}

export interface DayBend {
  windows: BendWindow[];
  /** A card's window: "3:30–5:00". */
  windowOf: (appointmentId: string) => BendWindow | null;
  /** "overlaps 1104 by 20 min" — per card, only where a start was chosen. */
  overlaps: Map<string, { id: string; unit: string; by: number }[]>;
  /** "Free 2:30–3:30 · Book something here". */
  gaps: FreeGap[];
  /** The crew's end of day (DAY_PLAN_END), minutes after midnight. */
  dayEnd: number;
  /** True when any card on the day has a chosen start: the day is then shown in start-time order. */
  bent: boolean;
}

/** `plan` is the live layout Today already has (useDayLayout); `live` false leaves the gaps out. */
export function useDayBend(day: string, visits: readonly TodayVisit[], plan: readonly PlannedPosition[], live = true): DayBend {
  const settings = useDayPlan().data?.settings;
  return useMemo(() => {
    const windows = windowsOf(plan, bendVisitsOf(visits));
    const p = dayPlanFor(day, settings ?? {});
    return {
      windows,
      windowOf: (id: string) => windows.find((w) => w.id === id) ?? null,
      overlaps: overlapsOnDay(windows),
      gaps: live ? freeGaps(windows, { nowMinutes: serviceMinutesNow(), gapMinutes: gapMinutes(settings ?? {}), plan: p }) : [],
      dayEnd: p.endMinutes,
      bent: windows.some((w) => w.chosen),
    };
  }, [day, visits, plan, settings, live]);
}
